import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Plus, Search, ClipboardList, Clock, CheckCircle2, AlertTriangle,
  LayoutGrid, List as ListIcon, X, ChevronRight, MessageSquare,
  User, MapPin, Calendar as CalIcon, Building2, Trash2, Send,
  Loader2, ArrowLeft, FileText, Sparkles, Users, SlidersHorizontal, Mail
} from 'lucide-react';
import WorkOrderAIWizard from './WorkOrderAIWizard';
import AssigneeContacts from './AssigneeContacts';
import WorkOrderAttachments from './WorkOrderAttachments';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authAxios = axios.create();
authAxios.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('adminToken');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const STATUSES = [
  { key: 'submitted',    label: 'Submitted',     color: 'bg-blue-500/20 text-blue-300 border-blue-400/30' },
  { key: 'under_review', label: 'Under Review',  color: 'bg-yellow-500/20 text-yellow-300 border-yellow-400/30' },
  { key: 'approved',     label: 'Approved',      color: 'bg-purple-500/20 text-purple-300 border-purple-400/30' },
  { key: 'in_progress',  label: 'In Progress',   color: 'bg-cyan-500/20 text-cyan-300 border-cyan-400/30' },
  { key: 'on_hold',      label: 'On Hold',       color: 'bg-orange-500/20 text-orange-300 border-orange-400/30' },
  { key: 'rejected',     label: 'Rejected',      color: 'bg-red-500/20 text-red-300 border-red-400/30' },
  { key: 'completed',    label: 'Completed',     color: 'bg-emerald-500/20 text-emerald-300 border-emerald-400/30' },
  { key: 'closed',       label: 'Closed',        color: 'bg-slate-500/20 text-slate-300 border-slate-400/30' },
];

const PRIORITIES = ['Low', 'Med', 'High', 'Urgent'];
const PRIORITY_COLORS = {
  Low: 'text-slate-300 bg-slate-500/20 border-slate-400/30',
  Med: 'text-yellow-300 bg-yellow-500/20 border-yellow-400/30',
  High: 'text-red-300 bg-red-500/20 border-red-400/30',
  Urgent: 'text-red-100 bg-red-600/40 border-red-400/60',
};
const DEPARTMENTS = ['Marketing', 'Driver', 'Finance', 'Sales', 'Operations', 'Technology', 'Legal'];

const statusMeta = (k) => STATUSES.find((s) => s.key === k) || STATUSES[0];

// -------- New Work Order Modal --------
const emptyForm = {
  request_date: new Date().toISOString().slice(0, 10),
  priority: 'Med',
  name: '',
  requested_by_department: 'Operations',
  business_client: '',
  departments_needed: [],
  location_for_service: '',
  instructions: '',
  date_due: '',
  work_description: '',
  contact_email: '',
  contact_phone: '',
  estimated_budget: '',
  attachment_urls: [],
};

const WorkOrderFormModal = ({ open, onClose, onCreated }) => {
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [attachmentInput, setAttachmentInput] = useState('');

  useEffect(() => { if (open) setForm(emptyForm); }, [open]);

  if (!open) return null;

  const toggleDept = (d) => {
    setForm((f) => ({
      ...f,
      departments_needed: f.departments_needed.includes(d)
        ? f.departments_needed.filter((x) => x !== d)
        : [...f.departments_needed, d],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const required = ['request_date','priority','name','requested_by_department','business_client','location_for_service','instructions','date_due','work_description'];
    for (const k of required) {
      if (!form[k] || (typeof form[k] === 'string' && !form[k].trim())) {
        toast.error(`Missing: ${k.replaceAll('_',' ')}`);
        return;
      }
    }
    if (form.departments_needed.length === 0) { toast.error('Select at least one Department Needed'); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        estimated_budget: form.estimated_budget === '' ? null : parseFloat(form.estimated_budget),
        contact_email: form.contact_email || null,
      };
      const { data } = await authAxios.post(`${API}/work-orders`, payload);
      toast.success(`Work order ${data.wo_number} created`);
      onCreated?.(data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create work order');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 backdrop-blur-sm overflow-y-auto p-0 sm:p-4" data-testid="wo-form-modal">
      <motion.form
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        onSubmit={submit}
        className="w-full sm:max-w-3xl min-h-full sm:min-h-0 sm:my-8 sm:rounded-2xl border-0 sm:border border-white/10 bg-[#0f172a] shadow-2xl"
      >
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-white/10 sticky top-0 z-10 bg-[#0f172a] sm:rounded-t-2xl">
          <div className="flex items-center gap-3">
            <ClipboardList className="w-5 h-5 text-cyan-400" />
            <div>
              <h2 className="text-lg font-semibold text-white">M.O.A. Work Order</h2>
              <p className="text-xs text-slate-400">Internal department use only</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-white" data-testid="wo-form-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-4 sm:px-6 py-5 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Request Date *">
              <input type="date" required value={form.request_date} onChange={(e) => setForm({ ...form, request_date: e.target.value })} className="input-dark w-full" data-testid="wo-request-date" />
            </Field>
            <Field label="Date Due *">
              <input type="date" required value={form.date_due} onChange={(e) => setForm({ ...form, date_due: e.target.value })} className="input-dark w-full" data-testid="wo-date-due" />
            </Field>
            <Field label="Priority *">
              <div className="flex gap-2">
                {PRIORITIES.map((p) => (
                  <button type="button" key={p} onClick={() => setForm({ ...form, priority: p })}
                    className={`px-3 py-2 rounded-lg text-sm border transition-all ${form.priority === p ? PRIORITY_COLORS[p] : 'border-white/10 text-slate-400 hover:border-white/20'}`}
                    data-testid={`wo-priority-${p}`}>
                    {p}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Name *">
              <input type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-dark w-full" placeholder="Your full name" data-testid="wo-name" />
            </Field>
            <Field label="Requested By (Department) *">
              <select required value={form.requested_by_department} onChange={(e) => setForm({ ...form, requested_by_department: e.target.value })} className="input-dark w-full" data-testid="wo-requested-by">
                {DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </Field>
            <Field label="Business / Client *">
              <input type="text" required value={form.business_client} onChange={(e) => setForm({ ...form, business_client: e.target.value })} className="input-dark w-full" placeholder="e.g. Acme Health" data-testid="wo-business" />
            </Field>
          </div>

          <Field label="Departments Needed *" hint="Multi-select — who needs to work on this?">
            <div className="flex flex-wrap gap-2">
              {DEPARTMENTS.map((d) => {
                const on = form.departments_needed.includes(d);
                return (
                  <button type="button" key={d} onClick={() => toggleDept(d)}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-all ${on ? 'bg-cyan-500/20 text-cyan-200 border-cyan-400/40' : 'border-white/10 text-slate-400 hover:border-white/20'}`}
                    data-testid={`wo-dept-${d}`}>
                    {d}
                  </button>
                );
              })}
            </div>
          </Field>

          <Field label="Location for Service *">
            <input type="text" required value={form.location_for_service} onChange={(e) => setForm({ ...form, location_for_service: e.target.value })} className="input-dark w-full" placeholder="Address, remote, HQ, etc." data-testid="wo-location" />
          </Field>

          <Field label="Instructions *">
            <textarea required rows={3} value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} className="input-dark w-full" placeholder="Any specific instructions or context…" data-testid="wo-instructions" />
          </Field>

          <Field label="Work Description *">
            <textarea required rows={4} value={form.work_description} onChange={(e) => setForm({ ...form, work_description: e.target.value })} className="input-dark w-full" placeholder="What needs to be done?" data-testid="wo-description" />
          </Field>

          <div className="pt-3 border-t border-white/5">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-3">Optional</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Contact Email">
                <input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="input-dark w-full" placeholder="you@medexova.com" data-testid="wo-contact-email" />
              </Field>
              <Field label="Contact Phone">
                <input type="tel" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className="input-dark w-full" data-testid="wo-contact-phone" />
              </Field>
              <Field label="Estimated Budget ($)">
                <input type="number" min="0" step="0.01" value={form.estimated_budget} onChange={(e) => setForm({ ...form, estimated_budget: e.target.value })} className="input-dark w-full" data-testid="wo-budget" />
              </Field>
            </div>
            <Field label="Attachment URLs" hint="Paste a link and press Add (files, screenshots, docs)">
              <div className="flex gap-2">
                <input type="url" value={attachmentInput} onChange={(e) => setAttachmentInput(e.target.value)} className="input-dark flex-1" placeholder="https://…" data-testid="wo-attach-input" />
                <button type="button" onClick={() => { if (attachmentInput) { setForm({ ...form, attachment_urls: [...form.attachment_urls, attachmentInput] }); setAttachmentInput(''); } }} className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white hover:bg-white/10" data-testid="wo-attach-add">Add</button>
              </div>
              {form.attachment_urls.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {form.attachment_urls.map((u, i) => (
                    <li key={i} className="flex items-center gap-2 text-xs text-slate-400">
                      <FileText className="w-3 h-3" />
                      <a href={u} target="_blank" rel="noreferrer" className="truncate hover:text-cyan-300 flex-1">{u}</a>
                      <button type="button" onClick={() => setForm({ ...form, attachment_urls: form.attachment_urls.filter((_, ix) => ix !== i) })} className="text-red-400 hover:text-red-300">
                        <X className="w-3 h-3" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </Field>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-4 sm:px-6 py-4 border-t border-white/10 bg-black/20 sm:rounded-b-2xl sticky bottom-0">
          <button type="button" onClick={onClose} className="px-4 min-h-[48px] rounded-lg text-slate-300 hover:text-white hover:bg-white/5">Cancel</button>
          <button type="submit" disabled={submitting} className="px-5 min-h-[48px] rounded-lg bg-cyan-500 text-black font-semibold hover:bg-cyan-400 disabled:opacity-50 flex items-center gap-2" data-testid="wo-form-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Submit Work Order
          </button>
        </div>
      </motion.form>
    </div>
  );
};

const Field = ({ label, hint, children }) => (
  <div>
    <label className="block text-xs font-medium text-slate-300 uppercase tracking-wider mb-1.5">{label}</label>
    {children}
    {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
  </div>
);

// -------- Detail Drawer --------
const WorkOrderDetail = ({ wo, onClose, onChanged }) => {
  const [comment, setComment] = useState('');
  const [transitionNote, setTransitionNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [nextStatus, setNextStatus] = useState('');
  const [tab, setTab] = useState('details');
  const [assigneeName, setAssigneeName] = useState(wo.assignee_name || '');
  const [contacts, setContacts] = useState(wo.assignee_contacts || []);

  useEffect(() => {
    setAssigneeName(wo.assignee_name || '');
    setContacts(wo.assignee_contacts || []);
  }, [wo]);

  const saveContacts = async (next) => {
    const previousCount = (wo.assignee_contacts || []).length;
    setContacts(next);
    try {
      const { data } = await authAxios.patch(`${API}/work-orders/${wo.id}`, { assignee_contacts: next });
      toast.success(next.length > previousCount ? 'Assignee added & emailed' : 'Assignees updated');
      onChanged?.(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update assignees');
      setContacts(wo.assignee_contacts || []);
    }
  };

  const saveAttachments = async (next) => {
    try {
      const { data } = await authAxios.patch(`${API}/work-orders/${wo.id}`, { attachments: next });
      onChanged?.(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save attachments');
    }
  };

  const doTransition = async () => {
    if (!nextStatus) { toast.error('Choose a status'); return; }
    setBusy(true);
    try {
      const { data } = await authAxios.post(`${API}/work-orders/${wo.id}/transition`, { to_status: nextStatus, note: transitionNote || null });
      toast.success(`Moved to ${nextStatus.replace('_',' ')}`);
      setTransitionNote(''); setNextStatus('');
      onChanged?.(data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Transition failed'); }
    finally { setBusy(false); }
  };

  const doComment = async () => {
    if (!comment.trim()) return;
    setBusy(true);
    try {
      const { data } = await authAxios.post(`${API}/work-orders/${wo.id}/comments`, { body: comment });
      setComment('');
      toast.success('Comment added');
      onChanged?.(data);
    } catch (err) { toast.error('Failed to add comment'); }
    finally { setBusy(false); }
  };

  const doAssign = async () => {
    setBusy(true);
    try {
      const { data } = await authAxios.patch(`${API}/work-orders/${wo.id}`, { assignee_name: assigneeName || null });
      toast.success('Assignee updated');
      onChanged?.(data);
    } catch (err) { toast.error('Failed to update assignee'); }
    finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (!window.confirm(`Delete work order ${wo.wo_number}?`)) return;
    try {
      await authAxios.delete(`${API}/work-orders/${wo.id}`);
      toast.success('Deleted');
      onChanged?.(null);
      onClose();
    } catch (err) { toast.error('Delete failed'); }
  };

  const s = statusMeta(wo.status);

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-2xl h-full bg-[#0f172a] sm:border-l border-white/10 overflow-y-auto overscroll-contain"
        data-testid="wo-detail-drawer">
        <div className="sticky top-0 z-10 bg-[#0f172a]/95 backdrop-blur border-b border-white/10 px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <button onClick={onClose} className="w-11 h-11 -ml-2 flex items-center justify-center text-slate-400 hover:text-white shrink-0" aria-label="Back" data-testid="wo-detail-close">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="min-w-0">
                <div className="text-xs text-slate-400">{wo.wo_number}</div>
                <div className="text-base sm:text-lg font-semibold text-white truncate">{wo.business_client}</div>
              </div>
            </div>
            <button onClick={doDelete} className="w-11 h-11 flex items-center justify-center text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg shrink-0" aria-label="Delete" data-testid="wo-delete-btn">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <span className={`px-3 py-1 rounded-full text-xs font-medium border ${s.color}`}>{s.label}</span>
            <span className={`px-2 py-1 rounded text-xs border ${PRIORITY_COLORS[wo.priority] || ''}`}>{wo.priority}</span>
            {(wo.assignee_contacts || []).length > 0 && (
              <span className="px-2 py-1 rounded text-xs border border-cyan-400/30 bg-cyan-500/10 text-cyan-200 flex items-center gap-1">
                <Users className="w-3 h-3" /> {wo.assignee_contacts.length} assigned
              </span>
            )}
          </div>
        </div>

        <div className="flex gap-1 px-4 sm:px-6 pt-3 border-b border-white/5 overflow-x-auto no-scrollbar">
          {['details','lifecycle','comments','history'].map((t) => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-4 min-h-[44px] text-sm capitalize border-b-2 transition-colors whitespace-nowrap ${tab === t ? 'text-cyan-300 border-cyan-400' : 'text-slate-400 border-transparent hover:text-white'}`}
              data-testid={`wo-tab-${t}`}>
              {t}
            </button>
          ))}
        </div>

        <div className="p-4 sm:p-6 space-y-6 pb-24">
          {tab === 'details' && (
            <div className="space-y-4">
              <Row icon={User} label="Requester" value={`${wo.name} (${wo.requested_by_department})`} />
              <Row icon={Building2} label="Business/Client" value={wo.business_client} />
              <Row icon={CalIcon} label="Requested" value={wo.request_date} />
              <Row icon={CalIcon} label="Due" value={wo.date_due} highlight />
              <Row icon={MapPin} label="Location" value={wo.location_for_service} />
              <Row label="Departments Needed" value={(wo.departments_needed || []).join(', ')} />
              {(wo.assignee_contacts || []).length > 0 && (
                <div>
                  <div className="text-xs text-slate-400 uppercase tracking-wider mb-1.5">Assignees</div>
                  <ul className="space-y-1.5">
                    {wo.assignee_contacts.map((c) => (
                      <li key={c.email} className="flex items-center gap-2 text-sm">
                        <span className="text-white">{c.name}</span>
                        <span className="text-slate-500 text-xs truncate">{c.email}</span>
                        {c.response && (
                          <span className={`ml-auto px-2 py-0.5 rounded text-[11px] border shrink-0 ${
                            c.response === 'declined'
                              ? 'border-red-400/40 bg-red-500/10 text-red-200'
                              : 'border-emerald-400/40 bg-emerald-500/10 text-emerald-200'
                          }`} data-testid={`wo-assignee-response-${c.email}`}>
                            {c.response}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div>
                <div className="text-xs text-slate-400 uppercase tracking-wider mb-2">Photos & Attachments</div>
                <WorkOrderAttachments attachments={wo.attachments || []} onChange={saveAttachments} />
              </div>
              {wo.contact_email && <Row label="Contact Email" value={wo.contact_email} />}
              {wo.contact_phone && <Row label="Contact Phone" value={wo.contact_phone} />}
              {wo.estimated_budget != null && <Row label="Estimated Budget" value={`$${Number(wo.estimated_budget).toFixed(2)}`} />}
              <div>
                <div className="text-xs text-slate-400 uppercase tracking-wider mb-1">Work Description</div>
                <div className="text-white whitespace-pre-wrap bg-white/5 rounded-lg p-3 text-sm">{wo.work_description}</div>
              </div>
              <div>
                <div className="text-xs text-slate-400 uppercase tracking-wider mb-1">Instructions</div>
                <div className="text-white whitespace-pre-wrap bg-white/5 rounded-lg p-3 text-sm">{wo.instructions}</div>
              </div>
              {(wo.attachment_urls || []).length > 0 && (
                <div>
                  <div className="text-xs text-slate-400 uppercase tracking-wider mb-1">Attachments</div>
                  <ul className="space-y-1">
                    {wo.attachment_urls.map((u, i) => (
                      <li key={i}><a href={u} target="_blank" rel="noreferrer" className="text-cyan-300 hover:underline text-sm break-all">{u}</a></li>
                    ))}
                  </ul>
                </div>
              )}
              {wo.resolution_notes && (
                <div>
                  <div className="text-xs text-slate-400 uppercase tracking-wider mb-1">Resolution Notes</div>
                  <div className="text-white whitespace-pre-wrap bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3 text-sm">{wo.resolution_notes}</div>
                </div>
              )}
            </div>
          )}

          {tab === 'lifecycle' && (
            <div className="space-y-5">
              <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4 text-cyan-300" />
                  <div className="text-xs text-slate-300 uppercase tracking-wider font-semibold">Assignee Contacts</div>
                </div>
                <AssigneeContacts contacts={contacts} onChange={saveContacts} compact />
              </div>

              <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                <div className="text-xs text-slate-400 uppercase tracking-wider mb-3">Assigned Team / Owner (label)</div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input type="text" value={assigneeName} onChange={(e) => setAssigneeName(e.target.value)} placeholder="e.g. Marketing Team" className="input-dark flex-1 min-h-[44px]" data-testid="wo-assignee-input" />
                  <button onClick={doAssign} disabled={busy} className="px-4 min-h-[44px] rounded-lg bg-white/10 text-white hover:bg-white/20" data-testid="wo-assignee-save">Save</button>
                </div>
              </div>

              <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                <div className="text-xs text-slate-400 uppercase tracking-wider mb-3">Move To Next Stage</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                  {STATUSES.filter((s) => s.key !== wo.status).map((s2) => (
                    <button key={s2.key} onClick={() => setNextStatus(s2.key)}
                      className={`px-3 min-h-[48px] rounded-lg text-sm border text-left transition-all ${nextStatus === s2.key ? s2.color + ' ring-2 ring-cyan-400/40' : 'border-white/10 text-slate-300 hover:border-white/20'}`}
                      data-testid={`wo-next-${s2.key}`}>
                      → {s2.label}
                    </button>
                  ))}
                </div>
                <textarea rows={2} placeholder="Optional note (visible in history & sent in status email)" value={transitionNote} onChange={(e) => setTransitionNote(e.target.value)} className="input-dark w-full mb-3" data-testid="wo-transition-note" />
                <button onClick={doTransition} disabled={busy || !nextStatus} className="w-full px-4 min-h-[48px] rounded-lg bg-cyan-500 text-black font-semibold hover:bg-cyan-400 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="wo-transition-confirm">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Confirm Status Change
                </button>
                <p className="text-xs text-slate-500 mt-2 text-center">
                  Emails info@medexova.com, the requester{contacts.length > 0 ? `, and ${contacts.length} assignee${contacts.length > 1 ? 's' : ''}` : ''}.
                </p>
              </div>
            </div>
          )}

          {tab === 'comments' && (
            <div className="space-y-4">
              <div className="space-y-3">
                {(wo.comments || []).length === 0 && <p className="text-sm text-slate-500 italic">No comments yet.</p>}
                {(wo.comments || []).map((c) => (
                  <div key={c.id} className="bg-white/5 rounded-lg p-3 border border-white/10">
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-sm text-white font-medium">{c.author_name}</div>
                      <div className="text-xs text-slate-500">{new Date(c.created_at).toLocaleString()}</div>
                    </div>
                    <div className="text-sm text-slate-300 whitespace-pre-wrap">{c.body}</div>
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add an internal note…" className="input-dark flex-1" data-testid="wo-comment-input" />
                <button onClick={doComment} disabled={busy || !comment.trim()} className="px-4 min-h-[48px] rounded-lg bg-cyan-500 text-black font-semibold hover:bg-cyan-400 disabled:opacity-50 flex items-center justify-center gap-1" data-testid="wo-comment-add">
                  <MessageSquare className="w-4 h-4" /> Post
                </button>
              </div>
            </div>
          )}

          {tab === 'history' && (
            <div className="space-y-2">
              {(wo.history || []).slice().reverse().map((h) => (
                <div key={h.id} className="bg-white/5 rounded-lg p-3 border border-white/10 text-sm">
                  <div className="flex items-center justify-between text-xs text-slate-400 mb-1">
                    <span className="capitalize font-medium text-slate-200">{h.action.replace('_',' ')}</span>
                    <span>{new Date(h.created_at).toLocaleString()}</span>
                  </div>
                  <div className="text-slate-300">
                    {h.actor_name}
                    {h.from_value && h.to_value && ` moved from `}
                    {h.from_value && <span className="text-slate-400">{h.from_value}</span>}
                    {h.from_value && h.to_value && ` → `}
                    {h.to_value && <span className="text-cyan-300">{h.to_value}</span>}
                    {h.note && <div className="text-slate-400 mt-1 text-xs italic">{h.note}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

const Row = ({ icon: Icon, label, value, highlight }) => (
  <div className="flex items-start gap-3">
    {Icon && <Icon className="w-4 h-4 text-slate-500 mt-0.5" />}
    <div className="flex-1">
      <div className="text-xs text-slate-400 uppercase tracking-wider">{label}</div>
      <div className={`text-sm ${highlight ? 'text-yellow-300 font-semibold' : 'text-white'}`}>{value || '—'}</div>
    </div>
  </div>
);

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="glass-card p-3 sm:p-4">
    <div className="flex items-center justify-between gap-2">
      <div className="min-w-0">
        <div className="text-[10px] sm:text-xs text-slate-400 uppercase tracking-wider truncate">{label}</div>
        <div className="text-xl sm:text-2xl font-bold text-white mt-0.5 sm:mt-1">{value ?? '—'}</div>
      </div>
      <Icon className={`w-6 h-6 sm:w-8 sm:h-8 shrink-0 ${color}`} />
    </div>
  </div>
);

// -------- Morning Digest bar --------
const DigestBar = () => {
  const [settings, setSettings] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [recipientsText, setRecipientsText] = useState('');

  const load = async () => {
    try {
      const { data } = await authAxios.get(`${API}/work-orders/digest/settings`);
      setSettings(data);
      setRecipientsText((data.recipients || []).join(', '));
    } catch {
      /* digest is optional chrome — stay quiet if it fails */
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setBusy(true);
    try {
      const { data } = await authAxios.put(`${API}/work-orders/digest/settings`, patch);
      setSettings(data);
      setRecipientsText((data.recipients || []).join(', '));
      toast.success('Digest schedule saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save digest settings');
    } finally {
      setBusy(false);
    }
  };

  const sendNow = async () => {
    setBusy(true);
    try {
      const { data } = await authAxios.post(`${API}/work-orders/digest/send-now`);
      toast.success(`Digest sent to ${data.sent} recipient${data.sent === 1 ? '' : 's'}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send digest');
    } finally {
      setBusy(false);
    }
  };

  if (!settings) return null;

  return (
    <div className="mb-5 rounded-xl border border-white/10 bg-white/[0.03] p-3" data-testid="wo-digest-bar">
      <div className="flex items-center gap-3 flex-wrap">
        <Mail className="w-4 h-4 text-cyan-300 shrink-0" />
        <div className="text-sm text-slate-300 flex-1 min-w-0">
          Morning digest{' '}
          <span className={settings.enabled ? 'text-emerald-300' : 'text-slate-500'}>
            {settings.enabled ? 'on' : 'off'}
          </span>{' '}
          · {String(settings.hour).padStart(2, '0')}:{String(settings.minute).padStart(2, '0')} {settings.timezone}
        </div>
        <button onClick={sendNow} disabled={busy} className="px-3 min-h-[40px] rounded-lg bg-white/10 border border-white/15 text-white text-sm disabled:opacity-50" data-testid="wo-digest-send-now">
          Send now
        </button>
        <button onClick={() => setOpen((v) => !v)} className="px-3 min-h-[40px] rounded-lg text-slate-300 text-sm hover:text-white" data-testid="wo-digest-toggle">
          {open ? 'Close' : 'Edit'}
        </button>
      </div>

      {open && (
        <div className="mt-3 pt-3 border-t border-white/10 grid grid-cols-1 sm:grid-cols-[auto_auto_1fr_auto] gap-2 items-end">
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1">Enabled</label>
            <button
              onClick={() => save({ enabled: !settings.enabled })}
              disabled={busy}
              className={`px-4 min-h-[44px] rounded-lg border text-sm ${settings.enabled ? 'bg-emerald-500/20 border-emerald-400/40 text-emerald-200' : 'bg-white/5 border-white/15 text-slate-400'}`}
              data-testid="wo-digest-enabled"
            >
              {settings.enabled ? 'On' : 'Off'}
            </button>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1">Send at</label>
            <select
              value={settings.hour}
              onChange={(e) => save({ hour: parseInt(e.target.value, 10) })}
              className="input-dark min-h-[44px]"
              data-testid="wo-digest-hour"
            >
              {Array.from({ length: 24 }, (_, h) => (
                <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1">Recipients (comma separated)</label>
            <input
              value={recipientsText}
              onChange={(e) => setRecipientsText(e.target.value)}
              className="input-dark w-full min-h-[44px]"
              data-testid="wo-digest-recipients"
            />
          </div>
          <button
            onClick={() => save({ recipients: recipientsText.split(',').map((s) => s.trim()).filter(Boolean) })}
            disabled={busy}
            className="px-4 min-h-[44px] rounded-lg bg-cyan-500 text-black font-semibold disabled:opacity-50"
            data-testid="wo-digest-save"
          >
            Save
          </button>
        </div>
      )}
    </div>
  );
};

// -------- Main --------
export const WorkOrders = () => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('board'); // 'board' | 'list'
  const [showForm, setShowForm] = useState(false);
  const [showAI, setShowAI] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;
      if (q) params.q = q;
      const [listRes, statRes] = await Promise.all([
        authAxios.get(`${API}/work-orders`, { params }),
        authAxios.get(`${API}/work-orders/stats`),
      ]);
      setItems(listRes.data.items || []);
      setStats(statRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load work orders');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, priorityFilter, q]);

  useEffect(() => { load(); }, [load]);

  const refreshAndSync = (updated) => {
    if (updated) {
      setItems((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
      setSelected(updated);
    }
    load();
  };

  return (
    <div>
      <div className="mb-5">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-white">M.O.A. Work Orders</h2>
          <p className="text-xs sm:text-sm text-slate-400">Internal requests · Emails to <span className="text-cyan-300">info@medexova.com</span></p>
        </div>
        <div className="flex gap-2 mt-4">
          <button
            onClick={() => setShowAI(true)}
            className="flex-1 sm:flex-none px-4 min-h-[48px] rounded-xl bg-gradient-to-r from-fuchsia-500 to-cyan-400 text-black font-semibold hover:opacity-90 flex items-center justify-center gap-2"
            data-testid="wo-ai-new-btn"
          >
            <Sparkles className="w-4 h-4" /> New with AI
          </button>
          <button
            onClick={() => setShowForm(true)}
            className="flex-1 sm:flex-none px-4 min-h-[48px] rounded-xl bg-white/10 border border-white/15 text-white font-semibold hover:bg-white/15 flex items-center justify-center gap-2"
            data-testid="wo-new-btn"
          >
            <Plus className="w-4 h-4" /> Manual Form
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 mb-5">
        <StatCard icon={ClipboardList} label="Total" value={stats?.total} color="text-cyan-400" />
        <StatCard icon={Clock} label="Active" value={(stats?.by_status?.submitted || 0) + (stats?.by_status?.under_review || 0) + (stats?.by_status?.approved || 0) + (stats?.by_status?.in_progress || 0)} color="text-yellow-400" />
        <StatCard icon={CheckCircle2} label="Completed" value={stats?.by_status?.completed || 0} color="text-emerald-400" />
        <StatCard icon={AlertTriangle} label="Overdue" value={stats?.overdue || 0} color="text-red-400" />
      </div>

      <DigestBar />

      <div className="mb-5 space-y-3">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search WO#, client…" className="w-full pl-10 input-dark min-h-[48px]" data-testid="wo-search" />          </div>
          <button
            onClick={() => setShowFilters((v) => !v)}
            className={`sm:hidden w-12 min-h-[48px] rounded-lg border flex items-center justify-center ${showFilters || statusFilter || priorityFilter ? 'bg-cyan-500/20 border-cyan-400/40 text-cyan-200' : 'bg-white/5 border-white/10 text-slate-400'}`}
            aria-label="Filters"
            data-testid="wo-filters-toggle"
          >
            <SlidersHorizontal className="w-4 h-4" />
          </button>
          <div className="hidden sm:flex bg-white/5 rounded-lg p-1 border border-white/10">
            <button onClick={() => setView('board')} className={`px-3 min-h-[40px] rounded-md text-sm flex items-center gap-1 ${view === 'board' ? 'bg-white/10 text-white' : 'text-slate-400'}`} data-testid="wo-view-board">
              <LayoutGrid className="w-4 h-4" /> Board
            </button>
            <button onClick={() => setView('list')} className={`px-3 min-h-[40px] rounded-md text-sm flex items-center gap-1 ${view === 'list' ? 'bg-white/10 text-white' : 'text-slate-400'}`} data-testid="wo-view-list">
              <ListIcon className="w-4 h-4" /> List
            </button>
          </div>
        </div>

        <div className={`${showFilters ? 'grid' : 'hidden'} sm:grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2`}>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="input-dark min-h-[48px]" data-testid="wo-filter-status">
            <option value="">All Statuses</option>
            {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} className="input-dark min-h-[48px]" data-testid="wo-filter-priority">
            <option value="">All Priorities</option>
            {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <div className="flex sm:hidden bg-white/5 rounded-lg p-1 border border-white/10">
            <button onClick={() => setView('board')} className={`flex-1 min-h-[40px] rounded-md text-sm flex items-center justify-center gap-1 ${view === 'board' ? 'bg-white/10 text-white' : 'text-slate-400'}`} data-testid="wo-view-board-mobile">
              <LayoutGrid className="w-4 h-4" /> Board
            </button>
            <button onClick={() => setView('list')} className={`flex-1 min-h-[40px] rounded-md text-sm flex items-center justify-center gap-1 ${view === 'list' ? 'bg-white/10 text-white' : 'text-slate-400'}`} data-testid="wo-view-list-mobile">
              <ListIcon className="w-4 h-4" /> List
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
      ) : items.length === 0 ? (
        <div className="glass-card p-10 text-center text-slate-400">
          <ClipboardList className="w-10 h-10 mx-auto mb-3 text-slate-600" />
          <p>No work orders yet.</p>
          <button onClick={() => setShowForm(true)} className="mt-4 px-4 py-2 rounded-lg bg-cyan-500 text-black font-semibold hover:bg-cyan-400">Create your first one</button>
        </div>
      ) : view === 'board' ? (
        <div className="flex md:grid md:grid-cols-2 xl:grid-cols-4 gap-3 md:gap-4 overflow-x-auto md:overflow-visible snap-x snap-mandatory -mx-4 px-4 md:mx-0 md:px-0 pb-2 no-scrollbar">
          {STATUSES.filter((s) => !['closed','rejected'].includes(s.key)).map((s) => {
            const cards = items.filter((i) => i.status === s.key);
            return (
              <div key={s.key} className="bg-white/5 rounded-xl border border-white/10 p-3 min-h-[160px] snap-start shrink-0 w-[82vw] sm:w-[60vw] md:w-auto" data-testid={`wo-col-${s.key}`}>
                <div className="flex items-center justify-between mb-3 px-1">
                  <div className={`px-2 py-1 rounded text-xs font-medium border ${s.color}`}>{s.label}</div>
                  <span className="text-xs text-slate-500">{cards.length}</span>
                </div>
                <div className="space-y-2">
                  {cards.map((wo) => (
                    <button key={wo.id} onClick={() => setSelected(wo)}
                      className="w-full text-left bg-[#0c1222] active:bg-white/10 hover:bg-white/5 border border-white/10 rounded-lg p-3 transition-colors"
                      data-testid={`wo-card-${wo.wo_number}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-slate-400">{wo.wo_number}</span>
                        <span className={`px-1.5 py-0.5 rounded text-xs border ${PRIORITY_COLORS[wo.priority] || ''}`}>{wo.priority}</span>
                      </div>
                      <div className="text-sm font-medium text-white truncate">{wo.business_client}</div>
                      <div className="text-xs text-slate-500 mt-1 line-clamp-2">{wo.work_description}</div>
                      <div className="text-xs text-slate-500 mt-2 flex items-center gap-2 flex-wrap">
                        <span className="flex items-center gap-1"><CalIcon className="w-3 h-3" /> Due {wo.date_due}</span>
                        {(wo.assignee_contacts || []).length > 0 && (
                          <span className="flex items-center gap-1 text-cyan-300/80"><Users className="w-3 h-3" /> {wo.assignee_contacts.length}</span>
                        )}
                      </div>
                    </button>
                  ))}
                  {cards.length === 0 && <p className="text-xs text-slate-600 text-center italic py-2">Empty</p>}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((wo) => {
            const s = statusMeta(wo.status);
            return (
              <button key={wo.id} onClick={() => setSelected(wo)}
                className="w-full text-left glass-card p-4 hover:border-white/20 active:bg-white/5 transition-all flex items-center gap-3"
                data-testid={`wo-row-${wo.wo_number}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs text-cyan-300">{wo.wo_number}</span>
                    <span className={`px-2 py-0.5 rounded text-xs border ${s.color}`}>{s.label}</span>
                    <span className={`px-1.5 py-0.5 rounded text-xs border ${PRIORITY_COLORS[wo.priority] || ''}`}>{wo.priority}</span>
                  </div>
                  <div className="font-semibold text-white mt-1 truncate">{wo.business_client} — {wo.name}</div>
                  <div className="text-sm text-slate-400 truncate">{wo.work_description}</div>
                  <div className="text-xs text-slate-500 mt-1 sm:hidden">Due {wo.date_due}</div>
                </div>
                <div className="text-right hidden sm:block">
                  <div className="text-xs text-slate-500">Due</div>
                  <div className="text-sm text-white">{wo.date_due}</div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-500 shrink-0" />
              </button>
            );
          })}
        </div>
      )}

      <WorkOrderFormModal open={showForm} onClose={() => setShowForm(false)} onCreated={() => load()} />
      <WorkOrderAIWizard open={showAI} onClose={() => setShowAI(false)} onCreated={() => load()} />
      {selected && <WorkOrderDetail wo={selected} onClose={() => setSelected(null)} onChanged={refreshAndSync} />}
    </div>
  );
};

export default WorkOrders;
